import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router";
import { LuCompass } from "react-icons/lu";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, aiServicePages, tech } from "../controller/global";
import { submitLead } from "../controller/leads";
import { getInterestPageContent } from "../controller/interestPageContent";

const PROCESS_STEPS = [
  { n: "01", t: "Tell us", d: "Share what you're trying to build through the form below." },
  { n: "02", t: "We assess fit", d: "We're upfront about whether this is something we can help with well — before you commit any budget." },
  { n: "03", t: "We scope together", d: "If it's a fit, we scope the engagement together rather than guessing at a quote." },
  { n: "04", t: "We build", d: "The same disciplined delivery process behind every service we already offer." },
];

const allTools = Array.from(new Set(tech.flatMap((category) => category.children.map((c) => c.name)))).slice(0, 14);

const FACTS = [
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" },
  { a: "11", b: "Core services offered" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Shared page for every "not yet a dedicated service" nav item (Blockchain,
// IoT, SaaS Development, CRM Development, etc.). Deliberately honest —
// title/blurb are the only per-item content, everything else (process,
// facts, cross-links) is real and generic, so no capability, stat, or case
// study is ever invented for an area we don't actually have a track record in.
const EmergingServiceInterest = ({ title, blurb }) => {
  const { pathname } = useLocation();
  const content = getInterestPageContent(pathname);
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = content?.metaTitle || `${title} | InQmise Technologies`;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = content?.metaDescription || blurb.slice(0, 160);
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://inqmisetechnologies.com${window.location.pathname}`);

    // Every route using this template renders the same "not yet a dedicated
    // service" page with only the title and blurb swapped — roughly 98%
    // identical markup across ~26 URLs. Indexed, that is a doorway-page
    // pattern, so keep them out of the index while still following their
    // links. When a service earns real content it gets its own page
    // component, and this template (and this tag) no longer applies to it.
    let robots = null;
    if (!content) {
      robots = document.querySelector("meta[name='robots'][data-stub]");
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        robots.setAttribute("data-stub", "true");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, follow");
    }

    // A page with its own content is a real page, so it gets the same schema
    // every other service page carries — Service and BreadcrumbList as well as
    // the FAQ markup.
    const schemaScripts = [];
    const addSchema = (data) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.textContent = JSON.stringify(data);
      document.head.appendChild(el);
      schemaScripts.push(el);
    };

    if (content?.serviceName) {
      const url = `https://inqmisetechnologies.com${window.location.pathname}`;
      addSchema({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": content.serviceName,
        "description": content.metaDescription,
        "url": url,
        "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
        "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
        "serviceType": content.serviceName,
      });
      addSchema({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
          { "@type": "ListItem", "position": 3, "name": content.serviceName, "item": url },
        ],
      });
    }

    let faqScript = null;
    if (content?.faqs?.length) {
      faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": content.faqs.map((item) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": { "@type": "Answer", "text": item.a },
        })),
      });
      document.head.appendChild(faqScript);
    }

    return () => {
      description.remove();
      robots?.remove();
      faqScript?.remove();
      for (const el of schemaScripts) el.remove();
    };
  }, [title, blurb, content]);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname": return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject": return value.trim() ? "" : "Subject is required.";
      case "description": return value.trim() ? "" : "Tell us a little about your project.";
      default: return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }
    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => setContactStatus("error"));
  };

  return (
    <main className="editorial-ai pt-20">
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">{content ? title : "Not yet a dedicated service"}</p>
          <h1>{content ? content.heading : `Have a ${title} project in mind?`}</h1>
          <p className="ea-lede">{content ? content.lede : blurb}</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={() => setIsOpen(true)} aria-label="Open contact form to tell us about your project">
              Tell us about your project ↗
            </button>
            <a className="ea-text-link" href="/services" onClick={(e) => { e.preventDefault(); navigate("/services"); }}>
              Explore all services
            </a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / {title.toUpperCase()} <b>{content ? "SERVICE" : "INQUIRY"}</b></div>
          <h2><LuCompass aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />We'll scope this together, honestly.</h2>
          {content ? (
            <>
              <div className="ea-query"><small>DELIVERY</small>The same seven stages as every other engagement, from a five-week MVP to a multi-quarter build.</div>
              <div className="ea-query"><small>HANDOVER</small>Your code, your repositories, your cloud accounts — documented, from day one.</div>
            </>
          ) : (
            <>
              <div className="ea-query"><small>STATUS</small>This isn't yet one of our dedicated services — but we regularly scope new kinds of work with clients.</div>
              <div className="ea-query"><small>APPROACH</small>Tell us what you need, and we'll be upfront about fit before anything is committed.</div>
            </>
          )}
          <div className="ea-flow"><b>Tell us</b><i>→</i><b>Assess</b><i>→</i><b>Scope</b></div>
        </Reveal>
      </header>

      {content && (
        <section className="ea-wrap ea-section" id="detail">
          <div className="esi-detail">
            {content.sections.map((block, i) => (
              <Reveal as="article" i={i} reduceMotion={reduceMotion} className="esi-block" key={block.heading}>
                <h2>{block.heading}</h2>
                {block.table && (
                  <div className="esi-table-wrap">
                    <table className="esi-table">
                      <caption>{block.table.caption}</caption>
                      <thead>
                        <tr>{block.table.head.map((cell, c) => <th key={c} scope="col">{cell}</th>)}</tr>
                      </thead>
                      <tbody>
                        {block.table.rows.map((row) => (
                          <tr key={row[0]}>
                            {row.map((cell, c) => (c === 0 ? <th key={c} scope="row">{cell}</th> : <td key={c}>{cell}</td>))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {block.checklist && (
                  <ol className="esi-checklist">
                    {block.checklist.map((item) => <li key={item}>{item}</li>)}
                  </ol>
                )}
                {block.body.map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* The stub template's generic process and stack sections. A page with
          its own content in interestPageContent.js tells that story in its own
          words, and repeating these blocks would put the same ~400 words on
          every such page — the duplication this rewrite exists to undo. */}
      {!content && (
        <>
        <section className="ea-services" id="process">
          <div className="ea-wrap ea-section">
            <Reveal as="div" className="ea-section-head">
              <div>
                <div className="ea-eyebrow">How this works</div>
                <h2>Four honest steps,<br />no assumptions.</h2>
              </div>
              <p>The same process we use before starting any new kind of engagement.</p>
            </Reveal>
            <div className="aa-process-line">
              {PROCESS_STEPS.map((step, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
                  <div className="aa-process-copy"><h3>{step.t}</h3><p>{step.d}</p></div>
                  <div className="aa-process-node">{step.n}</div>
                  <div />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="ea-models">
          <div className="ea-wrap ea-section">
            <Reveal as="div" className="ea-section-head">
              <div>
                <div className="ea-eyebrow">Who you'd be working with</div>
                <h2>Real stack,<br />real numbers.</h2>
              </div>
              <p>The same tools shown on /technologies, and the facts behind the team.</p>
            </Reveal>
            <div className="ea-model-layout">
              <Reveal as="div" className="ea-model-panel">
                <h3>Tools our team already works with</h3>
                <div className="ea-tags" style={{ padding: "8px 0 0" }}>
                  {allTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}
                </div>
              </Reveal>
              <div className="ea-metrics">
                {FACTS.map((m, i) => (
                  <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                    <strong>{m.a}</strong><span>{m.b}</span>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>
        </>
      )}

      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">What we already build</div>
            <h2>11 real services,<br />ready today.</h2>
          </div>
          <p>While we scope this together, here's what's already a proven, dedicated service.</p>
        </Reveal>
        <div className="cs-tags-row" style={{ marginBottom: 32 }}>
          {companyServices.map((s) => (
            <a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>
          ))}
          <a className="cs-tag" href="/services" style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate("/services"); }}>All Services →</a>
        </div>
        <div className="cs-tags-row">
          {aiServicePages.map((s) => (
            <a key={s.path} className="cs-tag" href={s.path} style={{ cursor: "pointer", textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate(s.path); }}>{s.title}</a>
          ))}
          <a className="cs-tag" href="/ai-solutions" style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}>All AI Solutions →</a>
        </div>
      </section>

      {content && (
        <section className="ea-wrap ea-section" id="faq">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Questions we get asked</div>
              <h2>Before you<br />brief anyone.</h2>
            </div>
            <p>The answers are the same ones we give on a first call — including where the honest answer is "it depends, and here is what it depends on".</p>
          </Reveal>
          <div className="esi-faq">
            {content.faqs.map((item, i) => (
              <Reveal as="div" i={i} reduceMotion={reduceMotion} className="esi-faq-row" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </Reveal>
            ))}
          </div>
          <div className="esi-related">
            <h3>Related</h3>
            <ul>
              {content.related.map((link) => (
                <li key={link.path}><Link to={link.path}>{link.label}</Link></li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="ea-cta" aria-labelledby="esi-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="esi-contact-heading">Ready to talk it through?</h2>
            <p>Tell us what you're building and we'll be honest about whether we're the right fit.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start the conversation.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label={`${title} project inquiry form`} className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="esi-fullname">Full Name</label>
                <input id="esi-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "esi-fullname-error" : undefined} />
                {contactErrors.fullname && <p id="esi-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="esi-phone">Phone Number (optional)</label>
                  <input id="esi-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "esi-phone-error" : undefined} />
                  {contactErrors.phone && <p id="esi-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="esi-email">Email</label>
                  <input id="esi-email" name="email" type="email" value={formData.email} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "esi-email-error" : undefined} />
                  {contactErrors.email && <p id="esi-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="esi-subject">Subject</label>
                <input id="esi-subject" name="subject" type="text" placeholder={`e.g., A ${title} project`} value={formData.subject} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "esi-subject-error" : undefined} />
                {contactErrors.subject && <p id="esi-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="esi-description">Project Details</label>
                <textarea id="esi-description" name="description" rows={3} value={formData.description} onChange={handleContactChange} aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "esi-description-error" : undefined} />
                {contactErrors.description && <p id="esi-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default EmergingServiceInterest;
